import { AskAbout } from '../amy/ask-about';
import { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Check, ChevronDown, ListChecks, Phone, PhoneOff, Sparkles, X } from 'lucide-react';
import { api, formatMoney } from '../api';
import { EmptyNote, formatDay, formatMoment } from '../components/desk';
import { Sheet } from '../components/ui/side-sheet';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { callActions, callChips, type CallAction, type CallToolEvent } from '../../../src/domain/voice/actions';
import '../customer-360.css';
import './calls.css';
import { useAgentName } from '../agent-name';

type Outcome = 'promise_to_pay' | 'payment_claim' | 'dispute' | 'wrong_contact' | 'call_back_later' | 'voicemail' | 'no_answer'
  | 'do_not_call' | 'transferred' | 'escalated' | 'email_requested' | 'talked' | 'failed' | 'message' | 'other';
type Sentiment = 'positive' | 'neutral' | 'negative';
interface CallRow {
  id: string; customerId: string | null; customerName: string; mode: 'test' | 'live'; status: string; outcome: Outcome | null;
  sentiment: Sentiment | null; summary: string | null; durationSeconds: number | null; createdAt: string; endedAt: string | null;
  invoiceNumbers: string[]; openTasks: number; hasRecording: boolean; events: CallToolEvent[];
  direction: 'outbound' | 'inbound'; callerNumber: string | null; verified: boolean;
}
interface CallDetail extends CallRow {
  invoices: { number: string; balanceMinor: string; currency: string; dueOn: string | null }[];
  endedReason: string | null; turns: { speaker: 'agent' | 'customer'; text: string }[]; recordingExpired: boolean;
  sentimentSource: 'model' | null;
  tasks: { id: string; title: string; kind: string; status: string; dueOn: string }[];
  promises: { id: string; amountMinor: string; currency: string; promisedOn: string; status: string }[];
  holds: { id: string; reason: string; until: string | null; active: boolean }[];
}

export const outcomeLabels: Record<Outcome, string> = {
  promise_to_pay: 'Promise to pay', payment_claim: 'Says already paid', dispute: 'Dispute', wrong_contact: 'Wrong contact',
  call_back_later: 'Call back later', voicemail: 'Voicemail left', no_answer: 'No answer', do_not_call: 'Do not call',
  transferred: 'Transferred', escalated: 'Asked for a person', email_requested: 'Asked for an email', talked: 'Talked',
  failed: 'Call failed', message: 'Took a message', other: 'Other',
};
const sentimentLabels: Record<Sentiment, string> = { positive: 'Positive', neutral: 'Neutral', negative: 'Negative' };
const duration = (seconds: number | null) => seconds === null ? '–' : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
const statusText: Record<string, string> = { planned: 'Planned', dialing: 'Dialing', in_progress: 'On the line', uncertain: 'Checking', cancelled: 'Cancelled' };

function OutcomeBadge({ call }: { call: Pick<CallRow, 'outcome' | 'status'> }) {
  if (!call.outcome) return <span className="call-outcome pending">{statusText[call.status] ?? call.status}</span>;
  return <span className={`call-outcome ${call.outcome}`}>{outcomeLabels[call.outcome]}</span>;
}
function SentimentDot({ value }: { value: Sentiment | null }) {
  if (!value) return <span className="muted">–</span>;
  return <span className={`call-sentiment ${value}`}><i aria-hidden />{sentimentLabels[value]}</span>;
}

interface Filters { days: number | null; outcome: string; sentiment: string; openTask: string }
const noFilters: Filters = { days: null, outcome: '', sentiment: '', openTask: '' };
const query = (filters: Filters, asOf: string | null, cursor: string | null) => {
  const { days, ...rest } = filters;
  const params = new URLSearchParams(Object.entries(rest).filter(([, v]) => v));
  if (days !== null && asOf) params.set('from', daysAgo(asOf, days));
  if (cursor) params.set('cursor', cursor);
  const text = params.toString();
  return `/app-api/calls${text ? `?${text}` : ''}`;
};

function ActionChips({ actions, limit = 3 }: { actions: CallAction[]; limit?: number }) {
  if (!actions.length) return null;
  const more = actions.length - limit;
  return <span className="call-chips">{actions.slice(0, limit).map(a =>
    <span key={a.id} className={`call-chip ${a.tone}`}>{a.text}</span>)}
    {more > 0 && <span className="call-chip more">+{more}</span>}</span>;
}
const clock = (iso: string) => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(iso));

/** Quick ranges are kept as a number of days, so the range stays right after midnight; "Any time" clears it. */
const ranges: { label: string; days: number | null }[] = [
  { label: 'Any time', days: null }, { label: 'Today', days: 0 }, { label: '7 days', days: 6 }, { label: '30 days', days: 29 },
];
/** Days back from the company's today (the server's `asOf`), as the server reads the from/to filters. */
const daysAgo = (asOf: string, days: number) => {
  const d = new Date(`${asOf}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

/** A pill that names its filter and current value, opening a short menu: the same control language as Collections. */
function FilterMenu({ label, any, value, options, onChange }: {
  label: string; any: string; value: string; options: Record<string, string>; onChange: (value: string) => void;
}) {
  return <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button type="button" className={`calls-filter-pill${value ? ' set' : ''}`} aria-label={`${label}: ${value ? options[value] : any}`}>
        <span>{label}</span>{value ? options[value] : 'Any'}<ChevronDown size={13} aria-hidden /></button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" sideOffset={6} className="calls-filter-menu">
      <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
        <DropdownMenuRadioItem value="">{any}</DropdownMenuRadioItem>
        {Object.entries(options).map(([key, text]) => <DropdownMenuRadioItem key={key} value={key}>{text}</DropdownMenuRadioItem>)}
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  </DropdownMenu>;
}

export function Calls({ initialId }: { initialId: string | null }) {
  const agent = useAgentName();
  const [filters, setFilters] = useState<Filters>(noFilters);
  const [rows, setRows] = useState<CallRow[] | null>(null); const [next, setNext] = useState<string | null>(null);
  const [error, setError] = useState(''); const [openId, setOpenId] = useState<string | null>(initialId);
  const [asOf, setAsOf] = useState<string | null>(null);
  const load = useCallback(async (cursor: string | null) => {
    try {
      // A range needs the company's today; the first, unfiltered read returns it.
      const today = asOf ?? (filters.days !== null ? (await api<{ asOf: string }>('/app-api/calls?limit=1')).asOf : null);
      const page = await api<{ asOf: string; rows: CallRow[]; nextCursor: string | null }>(query(filters, today, cursor));
      setAsOf(page.asOf);
      setRows(current => cursor && current ? [...current, ...page.rows] : page.rows); setNext(page.nextCursor); setError('');
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load calls'); }
  }, [filters, asOf]);
  useEffect(() => { void load(null); }, [load]);
  const open = (id: string | null) => {
    setOpenId(id); window.history.replaceState(null, '', id ? `#calls/${id}` : '#calls');
  };
  const set = (key: 'outcome' | 'sentiment' | 'openTask') => (value: string) => setFilters(f => ({ ...f, [key]: value }));
  const filtered = filters.days !== null || Boolean(filters.outcome || filters.sentiment || filters.openTask);
  return <main id="main-content" className="queue-page desk-page calls-page">
    <header className="page-heading"><div><h1>Calls<span className="heading-dot" aria-hidden="true">.</span></h1>
      <p>Every call {agent} placed: what was said, how it ended and what it set in motion.</p></div></header>
    <div className="calls-filters" role="group" aria-label="Filter calls">
      <div className="calls-range" role="group" aria-label="When">
        {ranges.map(r => <button key={r.days ?? 'any'} type="button" aria-pressed={filters.days === r.days}
          onClick={() => setFilters(f => ({ ...f, days: r.days }))}>{r.label}</button>)}
      </div>
      <FilterMenu label="Outcome" any="Any outcome" value={filters.outcome} onChange={set('outcome')} options={outcomeLabels} />
      <FilterMenu label="Sentiment" any="Any sentiment" value={filters.sentiment} onChange={set('sentiment')} options={sentimentLabels} />
      <FilterMenu label="Follow-up" any="All calls" value={filters.openTask} onChange={set('openTask')}
        options={{ true: 'Has an open task', false: 'Nothing open' }} />
      {filtered && <Button variant="ghost" size="sm" onClick={() => setFilters(noFilters)}>Clear</Button>}
    </div>
    {error && <div className="alert error" role="alert">{error}</div>}
    {rows && (rows.length ? <div className="desk-table-wrap"><table className="desk-table calls-table">
      <thead><tr><th>Customer</th><th>Outcome</th><th>What came of it</th><th>Sentiment</th><th className="numeric">Length</th><th>When</th></tr></thead>
      <tbody>{rows.map(call => <tr key={call.id} onClick={() => open(call.id)} className={call.openTasks ? 'has-open-task' : ''}>
        <td><button type="button" className="link-button" onClick={e => { e.stopPropagation(); open(call.id); }}>{call.customerName}</button>{call.customerId && <AskAbout customerId={call.customerId} name={call.customerName} />}
          {call.mode === 'test' && <span className="badge-test">Test</span>}
          {call.direction === 'inbound' && <span className="badge-test" title={call.verified ? 'They called us; caller verified' : 'They called us; caller not verified'}>Inbound</span>}
          {call.openTasks > 0 && <span className="call-open-task" aria-label={`${call.openTasks} open task${call.openTasks === 1 ? '' : 's'}`}><i aria-hidden /></span>}
          {call.invoiceNumbers.length > 0 && <small className="muted">{call.invoiceNumbers.join(', ')}</small>}</td>
        <td><OutcomeBadge call={call} /></td>
        <td className="call-came"><ActionChips actions={callChips(call.events)} />
          {call.summary ? <span className="call-summary" title={call.summary}>{call.summary}</span>
            : <span className="muted">No summary yet</span>}</td>
        <td><SentimentDot value={call.sentiment} /></td>
        <td className="numeric">{duration(call.durationSeconds)}</td>
        <td>{formatMoment(call.createdAt)}</td>
      </tr>)}</tbody>
    </table>{next && <Button variant="ghost" size="sm" className="timeline-more" onClick={() => void load(next)}>Show older calls</Button>}</div>
      : <EmptyNote icon={<Phone size={26} aria-hidden />} title={filtered ? 'No call matches these filters' : 'No calls yet'}>
        {filtered ? 'Clear a filter to see more.' : `Calls appear here as soon as ${agent} places one, with the recording, transcript and outcome.`}
      </EmptyNote>)}
    <CallSheet id={openId} onClose={() => open(null)} />
  </main>;
}

function CallSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const agent = useAgentName();
  const [call, setCall] = useState<CallDetail | null>(null); const [error, setError] = useState('');
  useEffect(() => {
    setCall(null); setError('');
    if (!id) return;
    void api<CallDetail>(`/app-api/calls/${encodeURIComponent(id)}`).then(setCall)
      .catch(e => setError(e instanceof Error ? e.message : 'Could not open the call'));
  }, [id]);
  const description = call ? { description: `${formatMoment(call.createdAt)} · ${duration(call.durationSeconds)}${call.mode === 'test' ? ' · test call' : ''}${call.direction === 'inbound' ? ` · they called${call.callerNumber ? ` from ${call.callerNumber}` : ''}${call.verified ? ', verified' : ', not verified'}` : ''}` } : {};
  const actions = call ? callActions(call.events) : [];
  const customer = call ? encodeURIComponent(call.customerId ?? '') : '';
  return <Sheet open={Boolean(id)} onClose={onClose} title={call ? `Call with ${call.customerName}` : 'Call'} {...description}
    icon={<Phone size={18} aria-hidden />}
    footer={call?.customerId ? <div className="call-sheet-links">
      <a className="button-link" href={`#collections/${customer}`}>Open the case in Collections <ArrowRight size={13} aria-hidden /></a>
      <a className="button-link" href={`#customers/${customer}`}>Customer page <ArrowRight size={13} aria-hidden /></a></div> : undefined}>
    {error && <div className="alert error" role="alert">{error}</div>}
    {call && <div className="call-detail">
      <div className="call-facts"><OutcomeBadge call={call} /><SentimentDot value={call.sentiment} />
        {call.sentimentSource === 'model' && <small className="muted">Sentiment read by AI</small>}</div>
      <ActionChips actions={callChips(call.events)} limit={6} />
      {call.hasRecording ? <audio className="call-player" controls preload="none" src={`/app-api/calls/${encodeURIComponent(call.id)}/recording`}>
        Your browser cannot play this recording.</audio>
        : <p className="muted call-no-recording"><PhoneOff size={14} aria-hidden />{call.recordingExpired ? 'The recording is past its retention period.' : 'No recording for this call.'}</p>}
      {call.summary && <section className="call-summary-card" aria-labelledby="call-summary-title">
        <h3 id="call-summary-title"><Sparkles size={13} aria-hidden />{agent}’s summary</h3><p>{call.summary}</p></section>}
      {actions.length > 0 && <section aria-labelledby="call-actions-title"><h3 id="call-actions-title">What {agent} did on the call</h3>
        <ol className="call-actions">{actions.map(a => <li key={a.id} className={a.ok ? a.tone : 'failed'}>
          <time dateTime={a.at}>{clock(a.at)}</time>
          <span className="call-action-mark" aria-hidden>{a.ok ? <Check size={12} /> : <X size={12} />}</span>
          <span>{a.text}{!a.ok && <small> · did not go through</small>}</span></li>)}</ol></section>}
      {call.invoices.length > 0 && <section aria-labelledby="call-invoices-title"><h3 id="call-invoices-title">Invoices discussed</h3>
        <table className="call-invoices"><caption className="sr-only">Invoice balances</caption>
          <thead className="sr-only"><tr><th scope="col">Invoice</th><th scope="col">Due</th><th scope="col">Balance</th></tr></thead><tbody>{call.invoices.map(i => <tr key={i.number}><td>{i.number}</td>
          <td className="muted">{i.dueOn ? `Due ${formatDay(i.dueOn)}` : ''}</td>
          <td className="numeric">{formatMoney(i.balanceMinor, i.currency as never)}</td></tr>)}</tbody></table>
        <small className="muted">Balances when the call was placed.</small></section>}
      {(call.promises.length > 0 || call.tasks.length > 0 || call.holds.length > 0) && <section aria-labelledby="call-records-title">
        <h3 id="call-records-title"><ListChecks size={13} aria-hidden />Records it created</h3><ul className="call-records">
        {call.promises.map(p => <li key={p.id}>Promise: {formatMoney(p.amountMinor, p.currency as never)} by {formatDay(p.promisedOn)} · {p.status}</li>)}
        {call.tasks.map(t => <li key={t.id}>Task: {t.title} · {t.status === 'open' ? `due ${formatDay(t.dueOn)}` : 'done'}</li>)}
        {call.holds.map(h => <li key={h.id}>Hold ({h.reason.replaceAll('_', ' ')}){h.until ? ` until ${formatDay(h.until)}` : ''}{h.active ? '' : ' · ended'}</li>)}
      </ul></section>}
      <section aria-labelledby="call-transcript-title"><h3 id="call-transcript-title">Transcript</h3>{call.turns.length
        ? <ol className="call-transcript">{call.turns.map((turn, index) => <li key={index} className={turn.speaker}>
          <span className="call-turn-who">{turn.speaker === 'agent' ? agent : call.customerName}</span>
          <p className="call-turn-text">{turn.text}</p></li>)}</ol>
        : <p className="muted">No transcript for this call.</p>}</section>
    </div>}
  </Sheet>;
}
